// Generated by Copilot- Start
import { resolveChannelLogoSrc } from "lib/helper/channel-logo-url";
import React, { useMemo } from "react";
import DtvModal from "@directv-digital/dds.ui.dtv-modal";
import { modalLinkPosition } from "src/utils/link-click";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "../../../video-lab/dtv-text";
import DtvDivider from "@directv-digital/dds.ui.dtv-divider";
import { ChannelCardV2, PackageSummary } from "lib/helper/channel-interactive-tool-v2";
import { IChnlInteractiveThemeUI } from "../../types";
import ModalChannelHeader from "../shared/ModalChannelHeader";
import ModalPackageSection from "../shared/ModalPackageSection";
import RsnTeamsTable from "./RsnTeamsTable";
import RsnTierLegalText from "./RsnTierLegalText";
import { buildModalPackageLists } from "../../utils/modal-package-builder";
import { parseModalContent } from "../../utils/modal-content-parser";
import { dtvModalStyle, dtvModalBodyStyle, dtvModalCloseStyle, dtvModalRootSx } from "../../styles/component-styles";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";

interface RsnChannelModalV2Props {
  open: boolean;
  channel?: ChannelCardV2;
  onClose: () => void;
  theme?: IChnlInteractiveThemeUI;
  packageSummaries?: Record<string, PackageSummary>;
  chnlSrc?: string;
  dmaData?: { dma: string; nielsenDma: string };
  rsnTierLegalText?: any;
  pkgModalContent?: any;
}

const RsnChannelModalV2: React.FC<RsnChannelModalV2Props> = ({
  open,
  channel,
  onClose,
  theme: _theme,
  packageSummaries = {},
  chnlSrc = "",
  dmaData: _dmaData,
  rsnTierLegalText,
  pkgModalContent
}) => {
  try {
    // Build package lists using centralized utility
    const { signaturePackageList, genrePackageList } = useMemo(
      () => buildModalPackageLists("rsn", {
        channel: channel!,
        packageSummaries,
        chnlSrc
      }),
      [channel, packageSummaries, chnlSrc]
    );

    // Determine if this is satellite variant
    const isSatellite = chnlSrc === "dtvClup";

    // Parse CMS content using centralized helper with package counts for singular/plural
    const { signaturePkgTxt, genrePkgTxt } = parseModalContent(pkgModalContent, isSatellite);

    // Get tier information from rawData
    const tierNumber = channel?.rawData?.rsntier || 1;
    const showTierSuperscript = tierNumber !== 1;

    if (!channel) {
      return null;
    }

    return (
      <DtvModal
        open={open}
        handleClose={onClose}
        // WAT-12797 (AC7): the close X reports the same modal_<category>_<name> as the CTAs inside.
        closeButtonProps={{ "data-linkposition": modalLinkPosition(`rsn_${channel?.label}`) }}
        modalStyle={dtvModalStyle}
        modalBodyStyle={dtvModalBodyStyle}
        sx={dtvModalRootSx}
        {...dtvModalCloseStyle}
      >
        <DtvBox sx={{
 display: "flex",
flexDirection: "column"
}}>
          {/* Channel Header with Logo */}
          {/* WAT-11362: resolveChannelLogoSrc is byte-identical for a relative path but leaves
              an absolute OV url alone — the old concat produced https://www.directv.com/https://… */}
          <ModalChannelHeader logo={channel.logo ? resolveChannelLogoSrc(channel.logo) : undefined} label={channel.label} isOvLogo={channel?.isOvLogo} />

          <DtvDivider sx={{
 width: "100% !important",
my: {
 xs: "18px",
sm: "21px"
}
}} />

          {/* Regional Sports Networks Section */}
          <DtvText content="Regional Sports Networks" variant="subhead2medium" sx={{
 fontSize: "20px !important",
mb: {
 xs: "16px",
sm: "21px"
}
}} />

          {/* Teams Table */}
          <RsnTeamsTable channel={channel} tierNumber={tierNumber} showTierSuperscript={showTierSuperscript} />

          {/* Tier Legal Text for tier 2/3 - shows AFTER table, BEFORE packages */}
          <RsnTierLegalText tierNumber={tierNumber} rsnTierLegalText={rsnTierLegalText} chnlSrc={chnlSrc} position="above-packages" />

          {/* Package Sections (Signature + Genre) */}
          <ModalPackageSection
            id="package-mapping-with-channel"
            heading={signaturePkgTxt}
            packages={signaturePackageList}
            channelName={channel.label}
            channelCategory="rsn"
            topMargin="0px"
            afterHeading={
              <RsnTierLegalText tierNumber={tierNumber} rsnTierLegalText={rsnTierLegalText} chnlSrc={chnlSrc} position="after-heading" />
            }
            beforePackages={
              <RsnTierLegalText tierNumber={tierNumber} rsnTierLegalText={rsnTierLegalText} chnlSrc={chnlSrc} position="before-packages" />
            }
          />

          {/* Genre Packages Section */}
          <ModalPackageSection
            id="package-mapping-with-channel-genre"
            heading={genrePkgTxt}
            packages={genrePackageList}
            channelName={channel.label}
            channelCategory="rsn"
            showDivider={signaturePackageList.length > 0}
            topMargin="26px"
            headingBottomMargin="16px"
          />
        </DtvBox>
      </DtvModal>
    );
  } catch (error) {
    console.error("Error in RsnChannelModalV2 >> ", error);
    return <ErrorDisplay error={error} component="RSN Channel Modal V2" />;
  }
};

export default React.memo(RsnChannelModalV2);
// Generated by Copilot- End
